// src/components/common/Sidebar.tsx
import React from 'react';
import {
  Box,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  Tooltip,
  useTheme,
} from '@mui/material';
import {
  RouteOutlined as RouteIcon,
  QueryStatsOutlined as ControlTowerIcon,
  CoPresent as TutorialIcon,
  PeopleAltOutlined as PeopleIcon,
  WarehouseOutlined as DepotIcon,
  LocalShippingOutlined as TripsIcon,
} from '@mui/icons-material';
import { TaskInfo } from '../../models/types';
import { useNavigate } from 'react-router-dom';
import { useHub } from '../../context/hub';
 
// Fixed sidebar width
const SIDEBAR_WIDTH = 48;

export interface SidebarProps {
  taskInfo?: TaskInfo | null;
  loading?: boolean;
  selectedTrips?: string[];
  onTripSelect?: (tripName: string) => void;
  onTripVisibilityToggle?: (tripName: string) => void;
  searchTerm?: string;
  onSearch?: (term: string) => void;
  onToggleMapOption?: (option: 'polygons' | 'sequence') => void;
  showPolygons?: boolean;
  showSequence?: boolean;
  onUserInfoClick?: () => void;
  onRoutePlanClick?: () => void;
  onUserManagementClick?: () => void;
  onDepotConfigurationClick?: () => void;
  onControlTowerClick?: () => void;
  onTripsClick?: () => void;
  activeItem?: 'routePlans' | 'trips' | 'userInfo' | 'userManagement' | 'depotConfiguration' | 'controlTower';
}

// Default props that can be spread when using the component
export const DEFAULT_SIDEBAR_PROPS: SidebarProps = {
  taskInfo: null,
  loading: false,
  selectedTrips: [],
  onTripSelect: () => {},
  onTripVisibilityToggle: () => {},
  searchTerm: '',
  onSearch: () => {},
  onToggleMapOption: () => {},
  showPolygons: true,
  showSequence: true,
  onUserInfoClick: () => {},
  onRoutePlanClick: () => {},
  onUserManagementClick: () => {},
  onDepotConfigurationClick: () => {},
  onControlTowerClick: () => {},
  onTripsClick: undefined,
  activeItem: 'routePlans',
};

const Sidebar: React.FC<SidebarProps> = ({
  taskInfo,
  loading,
  selectedTrips = [],
  onTripSelect = () => {},
  onTripVisibilityToggle = () => {},
  searchTerm = '',
  onSearch = () => {},
  onToggleMapOption = () => {},
  showPolygons = true,
  showSequence = true,
  onUserInfoClick = () => {},
  onRoutePlanClick = () => {},
  onUserManagementClick = () => {},
  onDepotConfigurationClick = () => {},
  onControlTowerClick = () => {},
  onTripsClick,
  activeItem = 'routePlans',
}) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { currentHubId } = useHub();
  const handleTripsClick = onTripsClick ?? (() => navigate(`/trips/hub/${currentHubId || 'default'}`));

  // Handle click on tutorial option
  const handleTutorialClick = () => {
    // Open YouTube tutorial in a new tab/window
    window.open('https://www.youtube.com/watch?v=exampleTutorial', '_blank');
  };

  return (
    <Box
      sx={{
        width: SIDEBAR_WIDTH,
        flexShrink: 0,
        borderRight: '1px solid',
        borderColor: 'divider',
        bgcolor: theme.palette.background.paper,
        height: '100%',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: theme.spacing(0, 1),
          height: 56, // Match toolbar height
        }}
      >
        {/* Logo or app icon could go here */}
      </Box>
      
      <List>
        {/* Route Plan Option */}
        <ListItem disablePadding sx={{ display: 'block' }}>
          <Tooltip title="Route Plans" placement="right">
            <ListItemButton
              sx={{
                minHeight: 48,
                justifyContent: 'center',
                px: 1.5,
              }}
              selected={activeItem === 'routePlans'}
              onClick={onRoutePlanClick}
            >
              <ListItemIcon
                sx={{
                  minWidth: 0,
                  justifyContent: 'center',
                }}
              >
                <RouteIcon />
              </ListItemIcon>
            </ListItemButton>
          </Tooltip>
        </ListItem>

        <ListItem disablePadding sx={{ display: 'block' }}>
          <Tooltip title="Trips" placement="right">
            <ListItemButton
              sx={{ minHeight: 48, justifyContent: 'center', px: 1.5 }}
              selected={activeItem === 'trips'}
              onClick={handleTripsClick}
            >
              <ListItemIcon sx={{ minWidth: 0, justifyContent: 'center' }}>
                <TripsIcon />
              </ListItemIcon>
            </ListItemButton>
          </Tooltip>
        </ListItem>

        {/* Control Tower */}
        <ListItem disablePadding sx={{ display: 'block' }}>
          <Tooltip title="Control Tower" placement="right">
            <ListItemButton
              sx={{
                minHeight: 48,
                justifyContent: 'center',
                px: 1.5,
              }}
              selected={activeItem === 'controlTower'}
              onClick={onControlTowerClick}
            >
              <ListItemIcon
                sx={{
                  minWidth: 0,
                  justifyContent: 'center',
                }}
              >
                <ControlTowerIcon />
              </ListItemIcon>
            </ListItemButton>
          </Tooltip>
        </ListItem>

        {/* User Management */}
        <ListItem disablePadding sx={{ display: 'block' }}>
          <Tooltip title="User Management" placement="right">
            <ListItemButton
              sx={{
                minHeight: 48,
                justifyContent: 'center',
                px: 1.5,
              }}
              selected={activeItem === 'userManagement'}
              onClick={onUserManagementClick}
            >
              <ListItemIcon
                sx={{
                  minWidth: 0,
                  justifyContent: 'center',
                }}
              >
                <PeopleIcon />
              </ListItemIcon>
            </ListItemButton>
          </Tooltip>
        </ListItem>

        {/* Depot Configuration */}
        <ListItem disablePadding sx={{ display: 'block' }}>
          <Tooltip title="Depot Configuration" placement="right">
            <ListItemButton
              sx={{
                minHeight: 48,
                justifyContent: 'center',
                px: 1.5,
              }}
              selected={activeItem === 'depotConfiguration'}
              onClick={onDepotConfigurationClick}
            >
              <ListItemIcon
                sx={{
                  minWidth: 0,
                  justifyContent: 'center',
                }}
              >
                <DepotIcon />
              </ListItemIcon>
            </ListItemButton>
          </Tooltip>
        </ListItem>
      </List>
      
      <List>
        {/* Help/Tutorial Option */}
        <ListItem disablePadding sx={{ display: 'block' }}>
          <Tooltip title="Tutorial" placement="right">
            <ListItemButton
              sx={{
                minHeight: 48,
                justifyContent: 'center',
                px: 1.5,
              }}
              onClick={handleTutorialClick}
            >
              <ListItemIcon
                sx={{
                  minWidth: 0,
                  justifyContent: 'center',
                }}
              >
                <TutorialIcon />
              </ListItemIcon>
            </ListItemButton>
          </Tooltip>
        </ListItem>
      </List>
    </Box>
  );
};

export default Sidebar;
